import { createReducer } from '@common/redux-utils';
import {
  AuthorizationSearchActionType,
  ChangeAuthoOwnershipStartAction,
  ChangeAuthoOwnershipSuccessAction,
  ChangeAuthoOwnershipFailureAction,
  GetAuthoStatusCountsStartAction,
  GetAuthoStatusCountsSuccessAction,
  GetAuthoStatusCountsFailureAction,
  GetTradesFailureAction,
  GetTradesStartAction,
  GetTradesSuccessAction,
  SearchAuthoListStartAction,
  SearchAuthoListSuccessAction,
  SearchAuthoListFailureAction,
  SetAuthorizationCountAction
} from './authorization-search.actions';
import { SwoAuthoReportSummarySearchViewModel } from '@models/AuthReportSummary';
import { StatusCountViewModel, TradeOptionsViewModel, AuthorizationCountViewModel } from '@models/ServiceWorkOrder';

// TODO: GET SWO AUTH VIEW MODEL
export interface AuthorizationSearchState {
  readonly authList: Array<SwoAuthoReportSummarySearchViewModel>;
  readonly loading: boolean;
  readonly message: string;
  readonly orderBy: string;
  readonly pageCount: number;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly query: string | null;
  readonly statusCounts?: StatusCountViewModel;
  readonly trade?: string;
  readonly priority?: string;
  readonly stateAbbr?: string;
  readonly vendorGroup?: string;
  readonly dispatchType?: string;
  readonly trades?: TradeOptionsViewModel[];
  readonly authorizationCount?: AuthorizationCountViewModel;
}

export const initialAuthorizationSearchState: AuthorizationSearchState = {
  authList: [],
  loading: false,
  message: '',
  orderBy: '',
  pageCount: 10,
  pageNumber: 0,
  pageSize: 10,
  query: null
};

/***** Get Autho Status Counts ****/
function handleGetAuthoStatusCountsFailure(
  state: AuthorizationSearchState,
  action: GetAuthoStatusCountsFailureAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false
  };
}

function handleGetAuthoStatusCountsStart(
  state: AuthorizationSearchState,
  action: GetAuthoStatusCountsStartAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: true
  };
}

function handleGetAuthoStatusCountsSuccess(
  state: AuthorizationSearchState,
  action: GetAuthoStatusCountsSuccessAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false,
    statusCounts: action.result
  };
}

/***** Get Trades ****/
function handleGetTradesFailure(
  state: AuthorizationSearchState,
  action: GetTradesFailureAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false
  };
}

function handleGetTradesStart(state: AuthorizationSearchState, action: GetTradesStartAction): AuthorizationSearchState {
  return {
    ...state,
    loading: true
  };
}

function handleGetTradesSuccess(
  state: AuthorizationSearchState,
  action: GetTradesSuccessAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false,
    trades: action.results
  };
}

/***** Search Dispatch List ****/
function handleSearchAuthoListFailure(
  state: AuthorizationSearchState,
  action: SearchAuthoListFailureAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false
  };
}

function handleSearchAuthoListStart(
  state: AuthorizationSearchState,
  action: SearchAuthoListStartAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: true,
    query: action.query,
    trade: action.trade === null ? undefined : action.trade,
    priority: action.priority === null ? undefined : action.priority,
    stateAbbr: action.stateAbbr === null ? undefined : action.stateAbbr,
    vendorGroup: action.vendorGroup === null ? undefined : action.vendorGroup,
    dispatchType: action.dispatchType === null ? undefined : action.dispatchType
  };
}

function handleSearchAuthoListSuccess(
  state: AuthorizationSearchState,
  action: SearchAuthoListSuccessAction
): AuthorizationSearchState {
  return {
    ...state,
    authList: action.results.results,
    loading: false,
    orderBy: action.orderBy,
    pageCount: action.results.totalResults <= 0 ? 0 : Math.ceil(action.results.totalResults / action.results.pageSize),
    pageNumber: action.pageNumber,
    pageSize: action.pageSize
  };
}

function handleSetAuthorizationCount(
  state: AuthorizationSearchState,
  action: SetAuthorizationCountAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false,
    authorizationCount: action.result
  };
}

function handleChangeAuthoOwnershipStart(
  state: AuthorizationSearchState,
  action: ChangeAuthoOwnershipStartAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: true
  };
}

function handleChangeAuthoOwnershipSuccess(
  state: AuthorizationSearchState,
  action: ChangeAuthoOwnershipSuccessAction
): AuthorizationSearchState {
  let newList: Array<SwoAuthoReportSummarySearchViewModel> = [];
  if (state.authList) {
    let list: Array<SwoAuthoReportSummarySearchViewModel> = state.authList;
    let result: SwoAuthoReportSummarySearchViewModel | undefined = list.find(x => {
      return x.swoId === action.swoId;
    });
    if (result) {
      const index = list.indexOf(result);
      if (index !== -1) {
        list[index] = action.release
          ? {
              ...result,
              ownerId: undefined,
              ownerFirstName: undefined,
              ownerLastName: undefined,
              ownerUserName: undefined
            }
          : {
              ...result,
              ownerId: action.user!.id,
              ownerFirstName: action.user!.firstName,
              ownerLastName: action.user!.lastName,
              ownerUserName: action.user!.email
            };
        // manually sets the owner after ownership change, since ES doesn't update fast enough
      }
      newList = list;
    }
  }
  return {
    ...state,
    loading: false,
    authList: [...newList]
  };
}
function handleChangeAuthoOwnershipFailure(
  state: AuthorizationSearchState,
  action: ChangeAuthoOwnershipFailureAction
): AuthorizationSearchState {
  return {
    ...state,
    loading: false
  };
}
export const AuthorizationSearchReducer = createReducer<AuthorizationSearchState, AuthorizationSearchActionType>(
  initialAuthorizationSearchState,
  {
    [AuthorizationSearchActionType.GetAuthoStatusCountsStart]: handleGetAuthoStatusCountsStart,
    [AuthorizationSearchActionType.GetAuthoStatusCountsSuccess]: handleGetAuthoStatusCountsSuccess,
    [AuthorizationSearchActionType.GetAuthoStatusCountsFailure]: handleGetAuthoStatusCountsFailure,
    [AuthorizationSearchActionType.GetTradesStart]: handleGetTradesStart,
    [AuthorizationSearchActionType.GetTradesSuccess]: handleGetTradesSuccess,
    [AuthorizationSearchActionType.GetTradesFailure]: handleGetTradesFailure,
    [AuthorizationSearchActionType.SearchAuthoListStart]: handleSearchAuthoListStart,
    [AuthorizationSearchActionType.SearchAuthoListSuccess]: handleSearchAuthoListSuccess,
    [AuthorizationSearchActionType.SearchAuthoListFailure]: handleSearchAuthoListFailure,
    [AuthorizationSearchActionType.SetAuthorizationCount]: handleSetAuthorizationCount,

    [AuthorizationSearchActionType.ChangeAuthoOwnershipStart]: handleChangeAuthoOwnershipStart,
    [AuthorizationSearchActionType.ChangeAuthoOwnershipSuccess]: handleChangeAuthoOwnershipSuccess,
    [AuthorizationSearchActionType.ChangeAuthoOwnershipFailure]: handleChangeAuthoOwnershipFailure
  }
);



// WEBPACK FOOTER //
// ./src/authorization/_components/authorization-search/authorization-search.reducer.ts